import { useState, type ReactNode } from 'react'
import { Icon } from './Icon'
import { EmptyState, ErrorState, Loading } from './ui'

/**
 * The table every admin screen uses.
 *
 * Deliberately one component rather than a table library: the screens here
 * need sticky headers, a search box, server-side paging and nothing else.
 * Anything fancier would be configuration for its own sake.
 */

export interface Column<T> {
  key: string
  header: ReactNode
  /** Right-aligned and tabular; use for anything numeric. */
  numeric?: boolean
  width?: string
  render: (row: T) => ReactNode
  /** Sort key sent to the server; omit to make the column unsortable. */
  sort?: string
}

interface Props<T> {
  columns: Column<T>[]
  rows: T[] | undefined
  rowKey: (row: T) => string
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  onRowClick?: (row: T) => void
  selectedKey?: string | null
  empty?: { title: string; body?: string; action?: ReactNode }
  /** Server-side paging. */
  total?: number
  limit?: number
  offset?: number
  onPage?: (offset: number) => void
  sort?: { key: string; desc: boolean }
  onSort?: (key: string, desc: boolean) => void
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  onRetry,
  onRowClick,
  selectedKey,
  empty,
  total,
  limit = 50,
  offset = 0,
  onPage,
  sort,
  onSort,
}: Props<T>) {
  if (loading) return <Loading />
  if (error) return <ErrorState error={error} retry={onRetry} />
  if (!rows?.length) {
    return (
      <EmptyState
        title={empty?.title ?? 'Nothing here yet'}
        body={empty?.body}
        action={empty?.action}
      />
    )
  }

  const showPager = total != null && total > limit && onPage

  return (
    <div className="flex flex-col min-h-0">
      <div className="overflow-auto min-h-0">
        <table className="grid-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={{ width: c.width, textAlign: c.numeric ? 'right' : undefined }}
                >
                  {c.sort && onSort ? (
                    <button
                      className="inline-flex items-center gap-1 hover:text-[var(--color-ink)] transition-colors"
                      onClick={() => onSort(c.sort!, sort?.key === c.sort ? !(sort?.desc ?? false) : false)}
                    >
                      {c.header}
                      {sort?.key === c.sort && (
                        <span style={{ color: 'var(--accent-text)' }}>{sort.desc ? '↓' : '↑'}</span>
                      )}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = rowKey(row)
              return (
                <tr
                  key={key}
                  data-selected={selectedKey === key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? 'cursor-pointer' : undefined}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={c.numeric ? 'tnum text-right' : undefined}
                      style={{ width: c.width }}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {showPager && (
        <div className="shrink-0 flex items-center justify-between gap-3 px-3.5 h-10 rule-t">
          <span className="label text-[10px]">
            {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <div className="flex gap-1.5">
            <button
              className="btn btn-sm"
              disabled={offset === 0}
              onClick={() => onPage(Math.max(0, offset - limit))}
            >
              <Icon name="chevron" size={12} className="rotate-180" />
              Previous
            </button>
            <button
              className="btn btn-sm"
              disabled={offset + limit >= total}
              onClick={() => onPage(offset + limit)}
            >
              Next
              <Icon name="chevron" size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** The search + filter strip that sits above a table. */
export function Toolbar({
  search,
  onSearch,
  placeholder = 'Search…',
  children,
  actions,
}: {
  search?: string
  onSearch?: (v: string) => void
  placeholder?: string
  children?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      {onSearch && (
        <div className="relative w-full sm:w-64">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-ink-4)]">
            <Icon name="search" size={14} />
          </span>
          <input
            className="field pl-8"
            placeholder={placeholder}
            value={search ?? ''}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
      )}
      {children}
      <div className="grow" />
      {actions}
    </div>
  )
}

/** Debounces a search box so every keystroke does not hit the API. */
export function useSearch(delay = 300) {
  const [value, setValue] = useState('')
  const [debounced, setDebounced] = useState('')

  useDebounce(() => setDebounced(value), delay, [value])

  return { value, debounced, setValue }
}

import { useEffect } from 'react'

function useDebounce(fn: () => void, delay: number, deps: unknown[]) {
  useEffect(() => {
    const t = window.setTimeout(fn, delay)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
